import * as React from 'react';
import { X } from 'lucide-react';
import { Controller, type FieldErrors, type UseFormReturn, useWatch } from 'react-hook-form';
import { AutocompleteMultiSelect, Button, Label } from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import { isAssignableShigoRole, ShigoRole } from '../../../../auth/permissions';
import { formatShigoRole } from '../../../../auth/roleLabels';
import { useAdminUserPublisherOptions } from '../../hooks';
import { AdminUsersContext, type AdminUserNetwork } from '../../model';
import { UserAgencyFields } from './UserAgencyFields';
import { clearAgencySelection } from './UserDialog.helpers';
import type { AdminUserFormValue } from './UserDialog.schema';

interface UserNetworkMembershipFieldsProps {
  form: UseFormReturn<AdminUserFormValue>;
  index: number;
  network: AdminUserNetwork;
  allowedRoles: {
    data?: string[] | undefined;
    isFetching: boolean;
    isError: boolean;
  };
  errors?: FieldErrors<AdminUserFormValue['memberships'][number]> | undefined;
  pending: boolean;
  onPublisherOptionsUnavailableChange: (networkId: string, unavailable: boolean) => void;
  onRemove: () => void;
}

export function UserNetworkMembershipFields({
  form,
  index,
  network,
  allowedRoles,
  errors,
  pending,
  onPublisherOptionsUnavailableChange,
  onRemove,
}: UserNetworkMembershipFieldsProps) {
  const membershipPath = `memberships.${String(index)}` as `memberships.${number}`;
  const agencyIdsPath = `${membershipPath}.agencyIds` as const;
  const rolesPath = `${membershipPath}.roles` as const;
  const publisherIdsPath = `${membershipPath}.publisherIds` as const;
  const roles = useWatch({ control: form.control, name: rolesPath });
  const publisherRoleSelected = roles.includes(ShigoRole.Publisher);
  const publishers = useAdminUserPublisherOptions({
    context: AdminUsersContext.SuperAdmin,
    networkId: String(network.id),
    enabled: publisherRoleSelected,
  });
  const initializedPublishers = React.useRef(false);
  const publisherOptionsUnavailable =
    publisherRoleSelected && (publishers.loading || publishers.error);

  React.useEffect(() => {
    onPublisherOptionsUnavailableChange(String(network.id), publisherOptionsUnavailable);
    return () => {
      onPublisherOptionsUnavailableChange(String(network.id), false);
    };
  }, [network.id, onPublisherOptionsUnavailableChange, publisherOptionsUnavailable]);

  React.useEffect(() => {
    if (
      initializedPublishers.current ||
      !publisherRoleSelected ||
      publishers.loading ||
      publishers.error
    ) {
      return;
    }
    initializedPublishers.current = true;
    const optionIds = new Set(publishers.options.map((publisher) => publisher.id));
    const current = form.getValues(publisherIdsPath);
    const scoped = current.filter((publisherId) => optionIds.has(publisherId));
    if (scoped.length !== current.length) {
      form.setValue(publisherIdsPath, scoped, { shouldDirty: false });
    }
  }, [
    form,
    publisherRoleSelected,
    publisherIdsPath,
    publishers.error,
    publishers.loading,
    publishers.options,
  ]);

  const roleOptions = (allowedRoles.data ?? []).filter(isAssignableShigoRole);

  return (
    <section className="border-border overflow-hidden rounded-lg border">
      <div className="border-border flex min-h-11 items-center justify-between gap-3 border-b px-4 py-2.5">
        <h3 className="min-w-0 truncate text-sm font-semibold">{network.name}</h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground h-7 w-7 shrink-0"
          disabled={pending}
          aria-label={`${dashboardT('users.removeNetwork')} ${network.name}`}
          onClick={onRemove}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-4 p-4">
        <div className="space-y-1.5">
          <Label className="text-[13px] font-semibold">
            {dashboardT('users.roles')} <span className="text-destructive">*</span>
          </Label>
          <Controller
            control={form.control}
            name={rolesPath}
            render={({ field }) => (
              <AutocompleteMultiSelect
                label={`${network.name} ${dashboardT('users.roles')}`}
                options={roleOptions.map((role) => ({ id: role, label: formatShigoRole(role) }))}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  clearAgencySelection(form, value, agencyIdsPath);
                  if (value.length > 0) {
                    form.clearErrors(rolesPath);
                  }
                  if (!value.includes(ShigoRole.Publisher)) {
                    form.setValue(publisherIdsPath, [], { shouldDirty: true });
                  }
                }}
                loading={allowedRoles.isFetching}
                disabled={allowedRoles.isFetching || allowedRoles.isError || pending}
                placeholder={dashboardT(
                  allowedRoles.isFetching ? 'users.rolesLoading' : 'users.selectRoles',
                )}
                emptyMessage={dashboardT('users.noAllowedRoles')}
                inlineSelectedValues
              />
            )}
          />
          {errors?.roles?.message ? (
            <p className="text-destructive text-xs">
              {translateValidationMessage(errors.roles.message)}
            </p>
          ) : null}
          {allowedRoles.isError ? (
            <p className="text-destructive text-xs">{dashboardT('users.rolesError')}</p>
          ) : null}
        </div>

        <UserAgencyFields form={form} disabled={pending} index={index} networkName={network.name} />
        {publisherRoleSelected ? (
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold">
              {dashboardT('users.publishers')} <span className="text-destructive">*</span>
            </Label>
            <Controller
              control={form.control}
              name={publisherIdsPath}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label={`${network.name} ${dashboardT('users.publishers')}`}
                  options={publishers.options.map((publisher) => ({
                    id: publisher.id,
                    label: publisher.name,
                  }))}
                  value={field.value}
                  onValueChange={field.onChange}
                  loading={publishers.loading}
                  disabled={pending}
                  placeholder={dashboardT('users.searchPublishers')}
                  emptyMessage={dashboardT('users.noPublishers')}
                  inlineSelectedValues
                />
              )}
            />
            {errors?.publisherIds?.message ? (
              <p className="text-destructive text-xs">
                {translateValidationMessage(errors.publisherIds.message)}
              </p>
            ) : null}
            {publishers.error ? (
              <p className="text-destructive text-xs">{dashboardT('users.publishersError')}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function translateValidationMessage(message: string) {
  return dashboardT(message as DashboardMessageKey);
}
